/**
 * Notification Broadcast Component
 * Allows admins to send push notifications to users
 */

import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TranslateIcon from '@mui/icons-material/Translate';

import { adminApi } from '../providers/dataProvider';
import { logger } from '../utils/logger';
import { UserAutocomplete, getAdminUserLabel, type AdminUserOption } from './shared/UserAutocomplete';

/**
 * Supported languages for notifications
 */
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
];

interface TranslationContent {
  title: string;
  message: string;
}

type Translations = Record<string, TranslationContent>;

export function NotificationBroadcast() {
  const notify = useNotify();

  // Form state
  const [target, setTarget] = useState<'all' | 'specific'>('all');
  const [selectedUsers, setSelectedUsers] = useState<AdminUserOption[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [translations, setTranslations] = useState<Translations>({
    en: { title: '', message: '' },
  });
  const [icon, setIcon] = useState('');
  const [route, setRoute] = useState('');
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [result, setResult] = useState<{ success: boolean; sentCount: number } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  /**
   * Handle translation content change
   */
  const handleTranslationChange = (
    lang: string,
    field: 'title' | 'message',
    value: string
  ) => {
    setTranslations((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      },
    }));
  };

  /**
   * Add a new language translation
   */
  const addLanguage = (langCode: string) => {
    if (!translations[langCode]) {
      setTranslations((prev) => ({
        ...prev,
        [langCode]: { title: '', message: '' },
      }));
    }
  };

  /**
   * Remove a language translation
   */
  const removeLanguage = (langCode: string) => {
    if (langCode === 'en') return; // Don't remove English
    const { [langCode]: _, ...rest } = translations;
    setTranslations(rest);
  };

  /**
   * Auto-translate English content to all supported languages
   */
  const handleAutoTranslate = async () => {
    const title = translations.en?.title?.trim();
    const message = translations.en?.message?.trim();

    if (!title || !message) {
      notify('Please enter English title and message first', { type: 'warning' });
      return;
    }

    setTranslating(true);
    try {
      const response = await adminApi.translateNotification(title, message);

      if (response.success && response.translations) {
        setTranslations(response.translations);
        // Set active tab to first language
        setActiveTab(0);
        notify('All translations generated successfully', { type: 'success' });
      }
    } catch (error) {
      notify('Failed to auto-translate. Please try again.', { type: 'error' });
      logger.error('Auto-translate error:', error);
    } finally {
      setTranslating(false);
    }
  };

  /**
   * Validate form before submission
   */
  const validateForm = (): string | null => {
    if (!translations.en?.title?.trim()) {
      return 'English title is required';
    }
    if (!translations.en?.message?.trim()) {
      return 'English message is required';
    }
    if (target === 'specific' && selectedUsers.length === 0) {
      return 'Please select at least one user';
    }
    return null;
  };

  /**
   * Handle initial submit - show confirmation
   */
  const handleSubmit = () => {
    const error = validateForm();
    if (error) {
      notify(error, { type: 'error' });
      return;
    }
    setShowConfirm(true);
  };

  /**
   * Handle confirmed submission
   */
  const handleConfirmedSubmit = async () => {
    setShowConfirm(false);
    setSending(true);
    setResult(null);

    try {
      const targetValue = target === 'all'
        ? 'all' as const
        : selectedUsers.map(user => user.id);

      const options: { icon?: string; route?: string } = {};
      if (icon.trim()) options.icon = icon.trim();
      if (route.trim()) options.route = route.trim();

      const response = await adminApi.broadcastNotification(
        targetValue,
        translations,
        Object.keys(options).length > 0 ? options : undefined
      );

      setResult(response);
      notify(`Notification sent to ${response.sentCount} users`, { type: 'success' });

      // Reset form on success
      setTranslations({ en: { title: '', message: '' } });
      setSelectedUsers([]);
      setIcon('');
      setRoute('');
    } catch (error) {
      notify('Failed to send notification', { type: 'error' });
      logger.error('Broadcast error:', error);
    } finally {
      setSending(false);
    }
  };

  const activeLanguages = Object.keys(translations);
  const availableLanguages = LANGUAGES.filter((l) => !translations[l.code]);

  return (
    <Box>
      <Title title="Broadcast Notification" />

      <Typography variant="h5" gutterBottom>
        Broadcast Notification
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Send push notifications to users
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Target Selection */}
          <Typography variant="h6" gutterBottom>
            Target Audience
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={sending}>
            <InputLabel id="broadcast-target-label">Target</InputLabel>
            <Select
              labelId="broadcast-target-label"
              value={target}
              label="Target"
              onChange={(e) => setTarget(e.target.value as 'all' | 'specific')}
            >
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="specific">Specific Users</MenuItem>
            </Select>
          </FormControl>

          {target === 'specific' && (
            <UserAutocomplete
              multiple
              label="Recipients"
              value={selectedUsers}
              onChange={setSelectedUsers}
              disabled={sending}
              testId="admin-broadcast-users"
              sx={{ mb: 2 }}
            />
          )}

          <Divider sx={{ my: 3 }} />

          {/* Translations */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Message Content
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={translating ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />}
                onClick={handleAutoTranslate}
                disabled={translating || sending || !translations.en?.title?.trim() || !translations.en?.message?.trim()}
              >
                {translating ? 'Translating...' : 'Auto Translate All'}
              </Button>
              {availableLanguages.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Add Language</InputLabel>
                  <Select
                    value=""
                    label="Add Language"
                    onChange={(e) => {
                      if (e.target.value) {
                        addLanguage(e.target.value);
                      }
                    }}
                  >
                    {availableLanguages.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ mb: 2 }}
          >
            {activeLanguages.map((langCode) => {
              const lang = LANGUAGES.find((l) => l.code === langCode);
              return (
                <Tab
                  key={langCode}
                  label={lang?.name || langCode.toUpperCase()}
                />
              );
            })}
          </Tabs>

          {activeLanguages.map((langCode, index) => (
            <Box
              key={langCode}
              hidden={activeTab !== index}
              sx={{ display: activeTab === index ? 'block' : 'none' }}
            >
              <TextField
                fullWidth
                label="Title"
                value={translations[langCode]?.title || ''}
                onChange={(e) => handleTranslationChange(langCode, 'title', e.target.value)}
                sx={{ mb: 2 }}
                required={langCode === 'en'}
              />

              <TextField
                fullWidth
                label="Message"
                value={translations[langCode]?.message || ''}
                onChange={(e) => handleTranslationChange(langCode, 'message', e.target.value)}
                multiline
                rows={4}
                sx={{ mb: 2 }}
                required={langCode === 'en'}
              />

              {langCode !== 'en' && (
                <Button
                  color="error"
                  size="small"
                  onClick={() => {
                    removeLanguage(langCode);
                    setActiveTab(0);
                  }}
                >
                  Remove {LANGUAGES.find((l) => l.code === langCode)?.name || langCode}
                </Button>
              )}
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          {/* Optional Fields */}
          <Typography variant="h6" gutterBottom>
            Optional Settings
          </Typography>

          <TextField
            fullWidth
            label="Icon"
            placeholder="notification-icon-name"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            helperText="Icon name for the notification"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Deep Link Route"
            placeholder="/home or /profile"
            value={route}
            onChange={(e) => setRoute(e.target.value)}
            helperText="Route to navigate when notification is tapped"
            sx={{ mb: 3 }}
          />

          {/* Result */}
          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully sent notification to {result.sentCount} users
            </Alert>
          )}

          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: target === 'all' ? 'warning.main' : 'primary.main' }}>
          Confirm Notification Broadcast
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            You are about to send a notification to{' '}
            <strong>
              {target === 'all'
                ? 'ALL USERS'
                : `${selectedUsers.length} specific users`}
            </strong>
            .
          </Typography>
          {target === 'specific' && <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {selectedUsers.map(getAdminUserLabel).join(', ')}
          </Typography>}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Preview (English):
            </Typography>
            <Typography variant="h6" color="text.primary">{translations.en?.title}</Typography>
            <Typography variant="body2" color="text.secondary">{translations.en?.message}</Typography>
          </Box>
          {target === 'all' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This notification will be sent to all registered users. This action cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSubmit}
            variant="contained"
            color={target === 'all' ? 'warning' : 'primary'}
          >
            {target === 'all' ? 'Send to All Users' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
